import { Anchor, Button, Divider, Flex, IconFont, Loading, Modal, SegmentedControl, Text, Toast, useDisclosure } from '@byecode/ui'
import { SpaceVersion } from '@lighthouse/core'
import { spaceVersionEnum, TagIcon, useSpaceQuotaContext } from '@lighthouse/shared'
import { startOfDay } from 'date-fns'
import React, { useCallback, useMemo, useRef, useState } from 'react'
import { useAsyncRetry } from 'react-use'
import styled from 'styled-components'

import { openSpaceGradeConfirm, SpaceGradeTag } from '@/components/SpaceGrade'
import WeChatAuditFormModal from '@/components/WeChatAuditFormModal'
import * as srv from '@/services'
import { WxProgramsAuditStatus, WxProgramsAuthorizationStatus } from '@/services/types'
import { useWxMiniProgramsAuthorizationResult } from '@/shared/reusable'

import { SharedHeader } from '../Share/Header'
import AuditInfo from './AuditInfo'
import { ProdQrcode } from './ProdQrcode'
import { TestQrcode } from './TestQrcode'

interface PublishWxProgramsProps {
    onBack: () => void
    onClose?: () => void
}

const SCxContainer = styled.div`
    width: 100%;
`

const SCxError = styled.div`
    padding: 6px 8px;
    line-height: 22px;
    border-radius: 4px;
    color: var(--color-red-500);
    background-color: var(--color-red-100);
`

const SCxContent = styled.div`
    width: 100%;
    margin-top: 20px;
    display: flex;
    flex-direction: column;
    gap: 16px;
    font-size: var(--font-size-normal);
`

const SCxUnpublished = styled.div`
    position: absolute;
    right: 6px;
    top: 6px;
    padding: 0 4px;
    border-radius: 4px;
    font-size: var(--font-size-sm);
    line-height: 20px;
    color: var(--color-gray-400);
    background-color: var(--color-gray-200);
`

const OPTIONS = [
    {
        label: '体验版',
        value: 'test'
    },
    {
        label: '正式版',
        value: 'prod'
    }
]

function binaryStringToBase64Image(binaryStr: string, mimeType = 'image/jpeg'): Promise<string> {
    return new Promise((resolve, reject) => {
        // 创建字节数组，每个字符对应一个字节
        const byteArray = new Uint8Array(binaryStr.length)
        for (let i = 0; i < binaryStr.length; i++) {
            const codePoint = binaryStr.codePointAt(i) ?? 0
            byteArray[i] = codePoint & 0xff
        }
        // 根据字节数组创建 Blob 对象
        const blob = new Blob([byteArray], { type: mimeType })
        // 使用 FileReader 将 Blob 转换为 data URL
        const reader = new FileReader()
        reader.onloadend = () => {
            resolve(reader.result as string)
        }

        reader.onerror = reject
        reader.readAsDataURL(blob)
    })
}

export const PublishWxPrograms: React.FunctionComponent<PublishWxProgramsProps> = ({ onBack, onClose }) => {
    // 授权的请求是否在加载中 或 是否在提交审核中
    const [loading, setLoading] = useState(false)
    const [environment, setEnvironment] = useState('test')
    const [opened, { close, open }] = useDisclosure(false)
    const timeRef = useRef<number>()
    const windowedRef = useRef<Window | null>(null)

    const { quota } = useSpaceQuotaContext()
    const { data: wxMiniProgramsAuthorization, update, loading: authorizationLoading } = useWxMiniProgramsAuthorizationResult()
    const { auditInfo, miniProgramInfo, authorizationStatus, releaseInfo, error } = wxMiniProgramsAuthorization || {}
    const isAuthorization = authorizationStatus === WxProgramsAuthorizationStatus.AUTH_SUCCESS
    // 是否微信审核通过
    const isWxProgramsPublished = releaseInfo?.published

    const { value: prodQrcode } = useAsyncRetry(async () => {
        if (!isWxProgramsPublished) {
            return
        }
        const arrayBuffer = await srv.getProductionQRCode()
        return binaryStringToBase64Image(arrayBuffer)
    }, [isWxProgramsPublished])

    const { value: testQrcode } = useAsyncRetry(async () => {
        if (!isAuthorization) {
            return
        }
        const res = await srv.getTrialQRCode()
        return binaryStringToBase64Image(res)
    }, [isAuthorization])

    const checkWxProgramsStatus = useCallback(async () => {
        // 超时或者用户手动关闭授权窗口时
        if ((timeRef.current && Date.now() - timeRef.current > 300000) || (windowedRef.current && windowedRef.current?.closed)) {
            timeRef.current = undefined
            setLoading(false)
            return
        }
        const res = await srv.getWxMiniProgramsAuthorizationResult()
        if (res.authStatus !== WxProgramsAuthorizationStatus.AUTHORIZING) {
            windowedRef.current?.close()
            windowedRef.current = null
            setLoading(false)
            update()
            return
        }
        setTimeout(() => checkWxProgramsStatus(), 3000)
    }, [update])

    const getWxProgramsAuthorizerUrl = useCallback(async () => {
        if (loading) {
            return
        }
        setLoading(true)
        const url = await srv.getPreAuthQRCode()
        if (url) {
            timeRef.current = Date.now()
            windowedRef.current = window.open(url, '_blank', 'width=800,height=600,resizable=no,scrollbars=no')
            checkWxProgramsStatus()
        }
    }, [checkWxProgramsStatus, loading])

    // 提供给测试用例使用，方便重置当前确认框的状态
    const enabledAuditCreateTimeRef = useRef(true)
    const handleAuditSubmit = useCallback(() => {
        if (auditInfo?.auditCreateTime) {
            const auditDay = startOfDay(new Date(auditInfo.auditCreateTime)).getTime()
            if (auditDay === startOfDay(new Date()).getTime() && enabledAuditCreateTimeRef.current) {
                Toast.error('每个自然日只能提交1次审核')
                return
            }
        }
        open()
    }, [auditInfo?.auditCreateTime, open])

    const handleRedoSubmit = useCallback(async () => {
        const isConfirm = await Modal.confirm({
            title: '确认撤销审核？',
            okStatus: 'primary',
            content: '每个自然日只能提交1次审核，撤销后今天不能再次提交审核。',
            cancelText: '再想想',
            okText: '确认',
            attribute: {
                'data-modal-ignore-click-away': true
            }
        })
        if (isConfirm) {
            const res = await srv.cancelMiniProgramAudit()
            if (res) {
                Toast.success('撤销成功')
                update()
            }
        }
    }, [update])

    const errContentEle = useMemo(() => {
        return (
            error && (
                <SCxError>
                    小程序授权失败： <br />
                    {error.msg && (
                        <>
                            {error.msg}
                            <br />
                        </>
                    )}
                    {error.spaceName && (
                        <>
                            {`空间名称：${error.spaceName}`} <br />
                        </>
                    )}
                    {error.appName && (
                        <>
                            {`应用名称：${error.appName}`}
                            <br />
                        </>
                    )}
                    详见：
                    <a
                        style={{ color: 'inherit', fontWeight: 600 }}
                        target="_blank"
                        href="https://byecodehelp.yuque.com/org-wiki-byecodehelp-zavfcl/oevich/yag2da08gd55spdn?singleDoc# "
                        rel="noreferrer"
                    >
                        帮助文档{'>'}
                    </a>
                </SCxError>
            )
        )
    }, [error])

    const auditInfoEle = useMemo(() => {
        switch (auditInfo?.status) {
            case WxProgramsAuditStatus.PASSED: {
                return null
            }
            case WxProgramsAuditStatus.AUDITING: {
                return (
                    <>
                        <Divider />
                        <AuditInfo onRedo={handleRedoSubmit} />
                    </>
                )
            }
            case WxProgramsAuditStatus.NOT_SUBMITTED:
            case WxProgramsAuditStatus.FAILED: {
                return (
                    <>
                        <Divider />
                        <Button
                            type="primary"
                            size="lg"
                            style={{ position: 'relative', alignItems: 'center' }}
                            suffix={<IconFont type="ArrowSquareIn" color="var(--color-white)" size={16} />}
                            block
                            loading={loading}
                            onClick={handleAuditSubmit}
                        >
                            提交微信审核
                        </Button>
                    </>
                )
            }
            default: {
                return null
            }
        }
    }, [auditInfo?.status, handleRedoSubmit, handleAuditSubmit, loading])

    const contentEle = useMemo(() => {
        if (authorizationLoading) {
            return (
                authorizationLoading && (
                    <SCxContent>
                        <Flex style={{ width: '100%', height: 60 }} alignItems="center" justifyContent="center">
                            <Loading outlined size={24} />
                        </Flex>
                        <Text align="center" color="var(--color-gray-400)" size={14}>
                            正在查询小程序授权信息
                        </Text>
                    </SCxContent>
                )
            )
        }
        if (isAuthorization) {
            return (
                <SCxContent>
                    <Flex justifyContent="space-between" alignItems="center">
                        <Flex direction="column" gap={8}>
                            <Text color="var(--color-black)" size={16} weight={500}>
                                已授权小程
                                <span
                                    onClick={() => {
                                        enabledAuditCreateTimeRef.current = !enabledAuditCreateTimeRef.current
                                    }}
                                >
                                    序
                                </span>
                            </Text>
                            <Text color="var(--color-gray-500)" size={14}>
                                名称：{miniProgramInfo?.name ?? '-'}的小程序{' '}
                            </Text>
                        </Flex>
                        <TagIcon size={40} iconSize={40} icon="MiniProgram" iconColor="var(--color-green-500)" />
                    </Flex>
                    <Divider />
                    <div style={{ width: '100%', position: 'relative' }}>
                        <SegmentedControl fullWidth data={OPTIONS} value={environment} onChange={setEnvironment} />
                        {!isWxProgramsPublished && <SCxUnpublished>未发布</SCxUnpublished>}
                    </div>
                    {environment === 'test' ? (
                        <TestQrcode qrcode={testQrcode ?? ''} />
                    ) : (
                        <>
                            <ProdQrcode qrcode={prodQrcode ?? ''} isWxProgramsPublished={isWxProgramsPublished} />
                            {auditInfoEle}
                        </>
                    )}
                    {errContentEle}
                </SCxContent>
            )
        }
        return (
            <SCxContent>
                <Flex justifyContent="space-between">
                    <Flex direction="column" gap={8}>
                        <Text color="var(--color-black)" size={16} weight={500}>
                            未授权小程序
                        </Text>
                        <Text color="var(--color-gray-500)" size={14}>
                            授权企业认证的小程序
                        </Text>
                    </Flex>
                    <Flex alignItems="center" gap={12}>
                        <TagIcon
                            size={40}
                            iconSize={24}
                            icon="MiniProgram"
                            background="var(--color-gray-100)"
                            style={{ border: '1.5px solid #fff' }}
                            iconColor="var(--color-gray-400)"
                            radius={6}
                        />
                        <TagIcon size={6} icon="Arrow4" iconColor="var(--color-gray-300)" />
                        <TagIcon
                            size={40}
                            iconSize={24}
                            icon="LogoGray"
                            background="var(--color-gray-100)"
                            iconColor="var(--color-gray-400)"
                            style={{ border: '1.5px solid #fff' }}
                            radius={6}
                        />
                    </Flex>
                </Flex>
                <Divider />
                <Text lineHeight="20px" align="center" color="var(--color-gray-400)">
                    还没有小程序 ？
                    <Anchor target="_blank" to="https://mp.weixin.qq.com/">
                        注册微信小程序{'>'}
                    </Anchor>
                </Text>
                <Button
                    type="primary"
                    size="lg"
                    style={{ position: 'relative', alignItems: 'center' }}
                    suffix={<IconFont type="ArrowSquareIn" color="var(--color-white)" size={16} />}
                    block
                    loading={loading}
                    onClick={() => {
                        if (quota && quota?.currentVersionCode !== undefined && quota?.currentVersionCode < spaceVersionEnum.ADVANCED) {
                            openSpaceGradeConfirm(SpaceVersion.ADVANCED)
                            return
                        }
                        getWxProgramsAuthorizerUrl()
                    }}
                >
                    授权小程序
                    {(quota?.currentVersionCode ?? spaceVersionEnum.FREE) < spaceVersionEnum.ADVANCED && (
                        <SpaceGradeTag
                            style={{
                                position: 'absolute',
                                right: 8,
                                top: 7,
                                background: 'var(--color-purple-200)'
                            }}
                            value={spaceVersionEnum.ADVANCED}
                        />
                    )}
                </Button>
                {errContentEle}
            </SCxContent>
        )
    }, [
        authorizationLoading,
        isAuthorization,
        loading,
        errContentEle,
        miniProgramInfo?.name,
        environment,
        isWxProgramsPublished,
        testQrcode,
        prodQrcode,
        auditInfoEle,
        quota,
        getWxProgramsAuthorizerUrl
    ])

    return (
        <SCxContainer>
            <SharedHeader onBack={onBack} onClose={onClose} title="发布为微信小程序" />
            {contentEle}
            <WeChatAuditFormModal
                open={opened}
                data={auditInfo}
                onClose={(isSubmit?: boolean) => {
                    close()
                    setLoading(!!isSubmit)
                }}
                onSubmit={() => {
                    setLoading(false)
                }}
            />
        </SCxContainer>
    )
}
